import type {
  InternalAxiosRequestConfig,
  AxiosRequestHeaders
} from "axios";
import { keycloak } from "@auth/keycloak";
import { isKeycloakEnabled } from "@configs/Env";
import {
  getAccessToken,
  isAccessTokenNearExpiry,
  canAttemptRequestRefresh,
  refreshAuthToken,
  setAccessToken
} from "@auth/tokenManager";
import { logKeycloakToken, tokenDebugInfo } from "@auth/authDebugLog";

/**
 * 요청 config 에 Keycloak Bearer 토큰을 주입한다.
 *
 * - Keycloak 비활성(localhost 등) 이면 아무것도 하지 않아 기존 쿠키 흐름 유지.
 * - 만료 임박 + 쿨다운 경과 시에만 갱신을 시도해 병렬 요청의 갱신 폭주를 방지.
 * - 파라미터를 변형하지 않고 새 config 를 반환한다.
 */
export async function attachAuthHeaders(
  config: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> {
  if (!isKeycloakEnabled()) return config;

  let token = getAccessToken();
  const memoryBefore = tokenDebugInfo(token);
  const keycloakToken = tokenDebugInfo(keycloak.token);

  // 콜백 직후 onTokens 와 요청 인터셉터 사이 타이밍 갭 대비.
  if (!token && keycloak.authenticated && keycloak.token) {
    token = keycloak.token;
    setAccessToken(token, "attachAuthHeaders.sync");
  }
  if (
    keycloak.authenticated &&
    isAccessTokenNearExpiry() &&
    canAttemptRequestRefresh()
  ) {
    try {
      token = await refreshAuthToken();
    } catch {
      token = getAccessToken();
    }
  }

  logKeycloakToken("attachAuthHeaders", {
    url: config.url,
    authenticated: keycloak.authenticated,
    memoryBefore,
    keycloakToken,
    attached: Boolean(token)
  });

  if (!token) return config;

  const headers = { ...config.headers } as AxiosRequestHeaders;
  headers.Authorization = `Bearer ${token}`;
  return { ...config, headers };
}
